import { Layout, Menu, MenuProps } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ThemeModeContext } from 'src/app/App';
import FooterComponent from 'src/app/components/FooterComponent/FooterComponent';
import styles from 'src/app/components/Sidebar/Sidebar.module.scss';
import { enumHelper, routeHelper } from 'src/app/helpers';
import { getEnumKeyByValue } from 'src/app/helpers/enum.helper';
import { genericExcludeFilter, getPermissionKey } from 'src/app/helpers/permission.helper';
import usePermissions from 'src/app/hooks/usePermissions';
import { ConfigContentStateType } from 'src/app/redux/configContent/configContentSlice';
import { useGetCustomersQuery } from 'src/app/redux/customers/customersSlice';
import { useAppSelector } from 'src/app/redux/hook';
import { routes } from 'src/configs/permissions.dictionary';
import { LAYOUTVIEW } from 'src/configs/system.config';
import { routesEnums } from 'src/enums';
import { RoutesEnum } from 'src/enums/route.enums';
import { LayoutViewType } from 'src/enums/user.enum';
import { IndustryIcon, LocationIcon, NetworkIcon, ReportIcon, ResourcesIcon, TopicIcon } from 'src/icons/index';
import { Sidebar as SidebarTheme } from 'src/theme/Sidebar.styled';
import { routesTypes } from 'src/types';
import { RoutesType } from 'src/types/routes.type';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

/* Function to get menu items in sidebar
It takes permissions parameter to check user permissions 
With the key parameter it gets the permission needed for that item 
*/
function getItem(
  permissions: string[],
  label: string,
  key: React.Key,
  icon?: React.ReactNode,
  excludeLayouts?: LayoutViewType[],
  children?: MenuItem[],
): MenuItem {
  // start
  // Get the permission needed for item
  const routeKey = getEnumKeyByValue(RoutesEnum, key.toString());
  const checkedPermission = getPermissionKey([routes[routeKey]]);
  // end

  if (excludeLayouts && !genericExcludeFilter(excludeLayouts, LAYOUTVIEW())) return null;
  if (!permissions.includes(checkedPermission)) return null;
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}
interface IProps {
  collapsed: boolean;
  setCollapsed: (active: boolean) => void;
}

const Sidebar = ({ collapsed, setCollapsed }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = usePermissions();
  const [sidebarMenu, setSidebarMenu] = useState<string>();

  const currentTheme: ConfigContentStateType = useAppSelector((state) => state.configContent);
  const { customerId } = useAppSelector((state) => state.user);

  const { mainTheme } = useContext(ThemeModeContext);

  const customer = useGetCustomersQuery(
    {
      ids: [customerId!],
    },
    { skip: !customerId },
  ).data?.rows[0];

  // Sidebar items
  const items: MenuItem[] = [
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.homePage'),
      RoutesEnum['/'],
      // <HomeIcon circleColor="transparent" color="var(--primary_text_color)" />,

      <img
        alt="home"
        src={`/sidebar/Home_3D_${sidebarMenu === routesEnums.RoutesEnum['/'] ? 'White' : mainTheme === 'light' ? 'Black' : 'White'}.png`}
        style={{
          height: '31px',
          width: '31px',
          margin: 'auto',
          background: sidebarMenu === RoutesEnum['/'] ? currentTheme.theme.colors.quoteBgc : undefined,
          borderRadius: '50%',
        }}
      />,
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.network'),
      RoutesEnum['/Network'],
      <NetworkIcon circleColor="transparent" color="var(--primary_text_color)" />,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.nsearch'),
      RoutesEnum['/NSearch'],
      // <SearchIcon
      //   style={{ height: '28px', width: '28px', marginLeft: '3px' }}
      //   circleColor="transparent"
      //   color="var(--primary_text_color)"
      // />,
      <img
        alt="nSearch"
        src={`/sidebar/Search_3D_${sidebarMenu === routesEnums.RoutesEnum['/NSearch'] ? 'White' : mainTheme === 'light' ? 'Black' : 'White'}.png`}
        style={{
          height: '31px',
          width: '31px',
          margin: 'auto',
          background: sidebarMenu === RoutesEnum['/NSearch'] ? currentTheme.theme.colors.quoteBgc : undefined,
          borderRadius: '50%',
        }}
      />,
      [LayoutViewType.SC3_LAYOUT],
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.myNotifications'),
      RoutesEnum['/Notifications'],
      // <NotificationIcon circleColor="transparent" color="var(--primary_text_color)" />,
      <img
        alt="notification"
        src={`/sidebar/Notification_3D_${sidebarMenu === routesEnums.RoutesEnum['/Notifications'] ? 'White' : mainTheme === 'light' ? 'Black' : 'White'}.png`}
        style={{
          height: '31px',
          width: '31px',
          margin: 'auto',
          background: sidebarMenu === RoutesEnum['/Notifications'] ? currentTheme.theme.colors.quoteBgc : undefined,
          borderRadius: '50%',
        }}
      />,
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.topic'),
      RoutesEnum['/Topic'],
      <TopicIcon circleColor="transparent" color="var(--primary_text_color)" />,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.location'),
      RoutesEnum['/Location'],
      <LocationIcon circleColor="transparent" color="var(--primary_text_color)" />,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.industry'),
      RoutesEnum['/Industry'],
      <IndustryIcon circleColor="transparent" color="var(--primary_text_color)" />,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),

    getItem(
      permissions,
      collapsed ? '' : t('sidebar.report'),
      RoutesEnum['/Reports'],
      <ReportIcon circleColor="transparent" color="var(--primary_text_color)" />,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.resources'),
      RoutesEnum['/Resources'],
      <ResourcesIcon circleColor="transparent" color="var(--primary_text_color)" />,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    // getItem(
    //   permissions,
    //   collapsed ? ''  : t("sidebar.customerAndUser"),
    //   RoutesEnum["/CustomerAndUser"],
    //   <CustomerAndUserIcon
    //     circleColor={"transparent"}
    //     color={"var(--primary_text_color)"}
    //   />
    // ),
    // getItem(
    //   permissions,
    //   collapsed ? ''  : t("sidebar.customerAndUserInvitations"),
    //   RoutesEnum["/CustomerAndUserInvitations"],
    //   <CustomerAndUserIcon
    //     circleColor={"transparent"}
    //     color={"var(--primary_text_color)"}
    //   />
    // ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.students'),
      RoutesEnum['/Students'],
      // <StudentIcon circleColor="transparent" color="var(--primary_text_color)" />,
      <img
        alt="student"
        src={`/sidebar/Student_3D_${sidebarMenu === routesEnums.RoutesEnum['/Students'] ? 'White' : mainTheme === 'light' ? 'Black' : 'White'}.png`}
        style={{
          height: '31px',
          width: '31px',
          margin: 'auto',
          background: sidebarMenu === RoutesEnum['/Students'] ? currentTheme.theme.colors.quoteBgc : undefined,
          borderRadius: '50%',
        }}
      />,
      [LayoutViewType.SC3_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    getItem(
      permissions,
      collapsed ? '' : t('sidebar.interests'),
      RoutesEnum['/Interests'],
      // <CustomerAndUserIcon circleColor="transparent" color="var(--primary_text_color)" />,
      <img
        alt="interests"
        src={`/sidebar/Interests_3D_${sidebarMenu === routesEnums.RoutesEnum['/Interests'] ? 'White' : mainTheme === 'light' ? 'Black' : 'White'}.png`}
        style={{
          height: '31px',
          width: '31px',
          margin: 'auto',
          background: sidebarMenu === RoutesEnum['/Interests'] ? currentTheme.theme.colors.quoteBgc : undefined,
          borderRadius: '50%',
        }}
      />,
      [LayoutViewType.SC3_LAYOUT],
    ),
  ];

  // Setting active item in sidebar to apply style on it
  useEffect(() => {
    const handleActiveTabInSidebar = () => {
      const routeEnum = `/${location.pathname.split('/')[1]}` as RoutesType;
      if (routesEnums.RoutesEnum[routeEnum] !== undefined) {
        setSidebarMenu(routesEnums.RoutesEnum[routeEnum]);
      } else {
        setSidebarMenu(undefined);
      }
    };

    handleActiveTabInSidebar();
  }, [location]);

  const onMenuClick: MenuProps['onClick'] = (menuItem) => {
    const route = enumHelper.getEnumKeyByValue(routesEnums.RoutesEnum, menuItem.key);
    routeHelper.handleRoute(navigate, route as routesTypes.RoutesType);
  };

  return (
    <SidebarTheme theme={currentTheme}>
      <div className={styles.sideBar}>
        <Sider
          collapsible
          breakpoint="lg"
          collapsedWidth={90}
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          trigger={null}
          width={275}
        >
          <div className={styles.menu} id="menu">
            <div id="menuContainer" className={styles.menuContainer}>
              <Menu mode="inline" items={items} onClick={onMenuClick} selectedKeys={sidebarMenu ? [sidebarMenu] : []} />
              {[LayoutViewType.NGINR_LAYOUT, LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.SAMA_BLACK_LAYOUT].includes(
                LAYOUTVIEW(),
              ) && (
                <div
                  style={{
                    color: currentTheme.theme.colors.quoteBgc,
                  }}
                  className={styles.classificationAndDbDiv}
                >
                  {!collapsed ? customer?.classification && <p>{customer.classification} </p> : null}
                  {customer?.dbVersion && <p> v{customer.dbVersion}</p>}
                </div>
              )}{' '}
            </div>

            <div className={styles.menuFooter} id="menuFooter">
              {collapsed ? (
                <img src="/logo_nginr_metalik_amblem.png" alt="Sama Logo" className={styles.nginrLogo} />
              ) : (
                <FooterComponent />
              )}
            </div>
          </div>
        </Sider>
      </div>
    </SidebarTheme>
  );
};

export default Sidebar;
